import React from "react";
import { Info, RefreshCw, Database, Globe2, Hammer, Award, Newspaper, Trophy, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, sub, color = "#7c4dff" }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-5">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5" style={{ color }} />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    </div>
    <div className="pixel-text mt-2 text-2xl text-white">{value}</div>
    {sub && <div className="mt-1 text-[11px] text-zinc-400">{sub}</div>}
  </div>
);

const Source = ({ name, url, freq, what }) => (
  <div className="rounded-xl border border-white/10 bg-[#10141b] p-4">
    <div className="flex items-center justify-between gap-2">
      <div className="font-semibold text-white">{name}</div>
      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
        {freq}
      </span>
    </div>
    <div className="mt-1 text-[11px] text-zinc-500">{what}</div>
    <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[11px] font-mono text-[#b39bff] hover:text-white">
      {url}
    </a>
  </div>
);

const About = () => (
  <section className="mx-auto max-w-6xl px-4 py-10" data-testid="about-page">
    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Über Habthek</div>
    <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold text-white"><Info className="h-7 w-7 text-[#b39bff]" /> Hinter den Pixeln</h1>
    <p className="mt-3 max-w-3xl text-sm text-zinc-400 leading-relaxed">
      Habthek ist eine <b>inoffizielle Fanseite</b> zur Geschichte von Habbo.de und allen anderen Hotels. Wir dokumentieren News, Events, Rares, Glitches und Legenden aus
      über 20 Jahren Habbo-Geschichte. Alle Daten beziehen wir live aus den offiziellen Habbo-APIs und ergänzenden Quellen wie habboassets.com.
    </p>

    {/* Update-Frequenzen */}
    <h2 className="mt-10 flex items-center gap-2 text-lg font-bold text-white"><RefreshCw className="h-5 w-5 text-emerald-300" /> Aktualisierungsraten</h2>
    <p className="mt-1 text-xs text-zinc-500">So oft holen wir Daten von externen Quellen neu rein.</p>
    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      <Source name="Habbo Gamedata-Texte"   freq="alle 6h"   what="Badge-Namen + Beschreibungen pro Hotel (DE/COM/...)" url="https://www.habbo.de/gamedata/external_flash_texts/0" />
      <Source name="Habbo Furnidata XML"    freq="alle 12h"  what="Möbel-Namen + Beschreibungen (offizielle Habbo-Daten)" url="https://www.habbo.de/gamedata/furnidata_xml/1" />
      <Source name="Habbo Avatare"          freq="live"      what="Habbo-Imaging: aktuelle Avatare via offizielle API" url="https://www.habbo.de/habbo-imaging/avatarimage" />
      <Source name="HabboAssets Badges"     freq="alle 30m"  what="Neueste Badges + Multi-Hotel-Suche via HabboAssets-API" url="https://www.habboassets.com/api" />
      <Source name="HabboAssets Furniture"  freq="alle 6h"   what="Möbel-Listing (200/Seite) gecacht beim Erstabruf" url="https://www.habboassets.com/furniture" />
      <Source name="Badge-Besitzer pro Hotel" freq="live (parallel)" what="9 Hotels gleichzeitig: /api/public/badge/owners/{code}" url="https://www.habbo.de/api/public/badge/owners" />
    </div>

    {/* Was wir tracken */}
    <h2 className="mt-12 flex items-center gap-2 text-lg font-bold text-white"><Database className="h-5 w-5 text-[#7c4dff]" /> Was wir auf Habthek tracken</h2>
    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
      <Stat icon={Newspaper} label="News-Artikel"     value="~50+" sub="kuratiert" color="#7c4dff" />
      <Stat icon={Trophy}    label="Events"           value="∞"    sub="inkl. Exklusiv-Markierung" color="#fbbf24" />
      <Stat icon={Building2} label="Projekte"         value="6"    sub="aktive Community-Teams" color="#10b981" />
      <Stat icon={Award}     label="Badges (live)"    value="alle" sub="9 Hotels parallel" color="#ec4899" />
      <Stat icon={Hammer}    label="Möbel (live)"     value="∞"    sub="20 neueste auf /sammlung" color="#ffb84d" />
      <Stat icon={Globe2}    label="Hotels"           value="9"    sub="DE, COM, BR, ES, FI, FR, IT, NL, TR" color="#22d3ee" />
      <Stat icon={Trophy}    label="Tier-Stufen"      value="10"   sub="Newbie → Ultra Legend" color="#a78bfa" />
      <Stat icon={Award}     label="Shop-Glows"       value="20"   sub="saisonal & limited" color="#f59e0b" />
    </div>

    {/* Quick Links */}
    <h2 className="mt-12 text-lg font-bold text-white">Schnellzugriff</h2>
    <div className="mt-4 flex flex-wrap gap-2">
      {[
        { to: "/news", label: "Aktuelle News" },
        { to: "/historie", label: "Zeitleiste" },
        { to: "/events", label: "Events" },
        { to: "/badges", label: "Badge-Datenbank" },
        { to: "/sammlung", label: "Möbel + Rares" },
        { to: "/shop", label: "Glow-Shop" },
        { to: "/leaderboard", label: "Leaderboard" },
        { to: "/projekte", label: "Projekte" },
      ].map((x) => (
        <Link key={x.to} to={x.to} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 hover:border-white/30 hover:bg-white/10">
          {x.label}
        </Link>
      ))}
    </div>

    {/* Rechtliches */}
    <h2 className="mt-12 text-lg font-bold text-white">Rechtliches</h2>
    <p className="mt-2 max-w-3xl text-xs text-zinc-500 leading-relaxed">
      Habthek ist nicht mit <b>Sulake / Azerion</b> verbunden. „Habbo“, „Habbo Hotel“ und das Habbo-Logo sind eingetragene Marken
      ihrer jeweiligen Rechteinhaber. Alle gezeigten Avatar-Bilder, Badges und Möbel-Grafiken sind Eigentum von Habbo / Sulake / Azerion
      und werden zu redaktionellen Zwecken (Fan-Berichterstattung, Historie) eingebunden.
    </p>
    <p className="mt-3 text-[11px] text-zinc-600">
      Siehe <Link to="/impressum" className="text-[#b39bff] hover:text-white">Impressum</Link> · <Link to="/datenschutz" className="text-[#b39bff] hover:text-white">Datenschutz</Link>
    </p>
  </section>
);

export default About;
